import { useState } from "react";
import { ArrowRight, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/site/PageHeader";
import { api, WHATSAPP_LINK } from "../lib/api";

const initial = {
  full_name: "", expertise: "", phone: "", email: "",
  city_district: "", education: "", has_certificate: "",
  experience: "", desired_branch: "", bio: "", cv_url: "",
};

export default function CoachApply() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/coach-applications", form);
      setSuccess(true);
      toast.success("Başvurunuz alındı!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Başvuru gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="coach-apply-page">
      <PageHeader
        eyebrow="Antrenör / Uzman Başvuru"
        title="Kulübümüze Katıl"
        subtitle="Focus Spor Kulübü olarak farklı spor branşlarını aktif hale getirmek için alanında uzman antrenörler, eğitmenler ve spor insanlarıyla iş birliği yapmak istiyoruz."
      />

      <section className="section-pad">
        <div className="container-x grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4 space-y-4 lg:sticky lg:top-28 self-start">
            <div className="card-elev p-6">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider mb-3">Neden Focus?</h3>
              <ul className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <li>· Çok branşlı, gelişen kulüp yapısı</li>
                <li>· Modern ekipman ve eğitim ortamı</li>
                <li>· Sürdürülebilir antrenör programı</li>
                <li>· Aktif marka ve görünürlük desteği</li>
                <li>· Tuzla — İstanbul merkezli güçlü ağ</li>
              </ul>
            </div>
            <div className="card-elev p-6">
              <h3 className="font-display text-lg font-bold uppercase tracking-wider mb-3">Hızlı İletişim</h3>
              <p className="text-sm text-muted-foreground mb-4">Önce konuşmak istersen, doğrudan WhatsApp:</p>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary w-full" data-testid="coach-whatsapp">
                <MessageCircle className="h-4 w-4" /> WhatsApp ile İletişim
              </a>
            </div>
          </aside>

          <div className="lg:col-span-8">
            {success ? (
              <div className="card-elev p-10 text-center" data-testid="coach-success">
                <div className="h-16 w-16 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center text-emerald-400 mb-5">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h2 className="heading-md">Başvurunuz Alındı</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  Focus Spor Kulübü ekibi başvurunuzu inceleyip en kısa sürede sizinle iletişime geçecektir.
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="btn-primary mt-6">
                  <MessageCircle className="h-4 w-4" /> WhatsApp'a Yönlendir
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="card-elev p-6 md:p-8" data-testid="coach-form">
                <div className="eyebrow mb-3">Başvuru Formu</div>
                <h2 className="heading-md mb-6">Bilgilerini Paylaş</h2>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Ad Soyad" required>
                    <input className="input-base" required value={form.full_name} onChange={update("full_name")} data-testid="coach-name" />
                  </Field>
                  <Field label="Uzmanlık Branşı" required>
                    <input className="input-base" required value={form.expertise} onChange={update("expertise")} data-testid="coach-expertise" />
                  </Field>
                  <Field label="Telefon" required>
                    <input className="input-base" required value={form.phone} onChange={update("phone")} data-testid="coach-phone" />
                  </Field>
                  <Field label="E-posta" required>
                    <input type="email" className="input-base" required value={form.email} onChange={update("email")} data-testid="coach-email" />
                  </Field>
                  <Field label="Şehir / İlçe" required>
                    <input className="input-base" required value={form.city_district} onChange={update("city_district")} data-testid="coach-city" />
                  </Field>
                  <Field label="Antrenörlük Belgesi" required>
                    <select className="input-base" required value={form.has_certificate} onChange={update("has_certificate")} data-testid="coach-cert">
                      <option value="">Seçiniz</option>
                      <option value="Var">Var</option>
                      <option value="Yok">Yok</option>
                      <option value="Süreçte">Süreçte</option>
                    </select>
                  </Field>
                  <Field label="Çalışmak İstediği Branş" required>
                    <input className="input-base" required value={form.desired_branch} onChange={update("desired_branch")} data-testid="coach-desired" />
                  </Field>
                  <Field label="CV / Portfolyo Linki">
                    <input className="input-base" placeholder="Drive / LinkedIn / PDF link" value={form.cv_url} onChange={update("cv_url")} data-testid="coach-cv" />
                  </Field>
                </div>

                <div className="grid gap-4 mt-4">
                  <Field label="Eğitim Geçmişi" required>
                    <textarea className="input-base min-h-[80px]" required value={form.education} onChange={update("education")} data-testid="coach-education" />
                  </Field>
                  <Field label="Sporcu / Antrenörlük Tecrübesi" required>
                    <textarea className="input-base min-h-[80px]" required value={form.experience} onChange={update("experience")} data-testid="coach-experience" />
                  </Field>
                  <Field label="Kısa Açıklama" required>
                    <textarea className="input-base min-h-[100px]" required placeholder="Kendinizden, çalışma tarzınızdan ve hedeflerinizden bahsedin." value={form.bio} onChange={update("bio")} data-testid="coach-bio" />
                  </Field>
                </div>

                <button type="submit" disabled={submitting} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="coach-submit">
                  {submitting ? "Gönderiliyor..." : "Başvuruyu Gönder"} {!submitting && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-input">{label}{required && <span className="text-primary"> *</span>}</span>
      {children}
    </label>
  );
}
